/**
 * Environment Configuration Module
 * Flexy: Centralized environment variable access with type safety
 * 
 * This module provides a single source of truth for all environment-based
 * configuration values, enabling multi-tenant deployments and eliminating
 * hardcoded school-specific values.
 */

export const ENV = {
  SCHOOL: {
    NAME: import.meta.env.VITE_SCHOOL_NAME || '',
    NPSN: import.meta.env.VITE_SCHOOL_NPSN || '',
    ADDRESS: import.meta.env.VITE_SCHOOL_ADDRESS || '',
    PHONE: import.meta.env.VITE_SCHOOL_PHONE || '',
    EMAIL: import.meta.env.VITE_SCHOOL_EMAIL || '',
    WEBSITE: import.meta.env.VITE_SCHOOL_WEBSITE || '',
    FOUNDING_YEAR: import.meta.env.VITE_SCHOOL_FOUNDING_YEAR || '2000',
    CONTACTS: {
      ADMIN: import.meta.env.VITE_CONTACT_ADMIN || '',
      GURU: {
        STAFF: import.meta.env.VITE_CONTACT_GURU_STAFF || '',
        BIASA: import.meta.env.VITE_CONTACT_GURU_BIASA || '',
      },
      SISWA: {
        OSIS: import.meta.env.VITE_CONTACT_SISWA_OSIS || '',
        BIASA: import.meta.env.VITE_CONTACT_SISWA_BIASA || '',
      },
    },
  },
  API: {
    BASE_URL: import.meta.env.VITE_API_BASE_URL || '',
  },
  EMAIL: {
    ADMIN: import.meta.env.VITE_ADMIN_EMAIL || '',
  },
  EXTERNAL: {
    GOOGLE_FONTS_INTER: import.meta.env.VITE_GOOGLE_FONTS_INTER || 'https://fonts.googleapis.com/css2?family=Inter:wght@100..900&display=swap',
    GOOGLE_FONTS_JETBRAINS: import.meta.env.VITE_GOOGLE_FONTS_JETBRAINS || 'https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@100..800&display=swap',
    RDM_PORTAL: String(import.meta.env.VITE_RDM_PORTAL || ''), // Flexy: No hardcoded school URL
    // Flexy: External API and service URLs - ENV-driven for multi-tenant support
    MAKER_SUITE_API: String(import.meta.env.VITE_MAKER_SUITE_API || 'https://makersuite.google.com/app/apikey'),
    PLACEHOLDER_IMAGE_BASE: String(import.meta.env.VITE_PLACEHOLDER_IMAGE_BASE || 'https://placehold.co/600x400?text='),
  },
} as const;

// School Legal Documents Configuration
export const SCHOOL_DOCUMENTS = {
  SK_PENDIRIAN: {
    NUMBER: import.meta.env.VITE_SK_PENDIRIAN_NUMBER || 'D/Wi/MA./101/2000',
    DATE: import.meta.env.VITE_SK_PENDIRIAN_DATE || '20-09-2000',
  },
  SK_OPERASIONAL: {
    NUMBER: import.meta.env.VITE_SK_OPERASIONAL_NUMBER || 'D/Wi/MA./101/2000',
    DATE: import.meta.env.VITE_SK_OPERASIONAL_DATE || '20-09-2000',
  },
} as const;

import type { UserRole, UserExtraRole } from '../types';

export interface MockTeacher {
  id: string;
  name: string;
  email: string;
  subjects: string[];
}

export interface MockUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  extraRole?: UserExtraRole;
}

const getEmailDomain = () => ENV.SCHOOL.EMAIL.split('@')[1] || 'school.com';

export const MOCK_CONTACTS: { TEACHERS: MockTeacher[]; USERS: MockUser[] } = {
  TEACHERS: [
    { id: 'teacher1', name: 'Ahmad Wijaya', email: `ahmad@${getEmailDomain()}`, subjects: ['Matematika', 'Fisika'] },
    { id: 'teacher2', name: 'Siti Nurhaliza', email: `siti@${getEmailDomain()}`, subjects: ['Bahasa Indonesia', 'Sastra'] },
    { id: 'teacher3', name: 'Budi Santoso', email: `budi@${getEmailDomain()}`, subjects: ['IPA', 'Biologi'] },
    { id: 'teacher4', name: 'Dewi Lestari', email: `dewi@${getEmailDomain()}`, subjects: ['IPS', 'Sejarah'] },
    { id: 'teacher5', name: 'Eko Prasetyo', email: `eko@${getEmailDomain()}`, subjects: ['Bahasa Inggris', 'Seni'] },
  ],
  USERS: [
    { id: 'user1', name: 'Ahmad Wijaya', email: `ahmad@${getEmailDomain()}`, role: 'teacher' as UserRole },
    { id: 'user2', name: 'Siti Nurhaliza', email: `siti@${getEmailDomain()}`, role: 'teacher' as UserRole },
    { id: 'user3', name: 'Budi Santoso', email: `budi@${getEmailDomain()}`, role: 'teacher' as UserRole, extraRole: 'wakasek' as UserExtraRole },
    { id: 'user4', name: 'Dewi Lestari', email: `dewi@${getEmailDomain()}`, role: 'teacher' as UserRole },
    { id: 'user5', name: 'Eko Prasetyo', email: `eko@${getEmailDomain()}`, role: 'teacher' as UserRole },
    { id: 'user6', name: 'Fitri Handayani', email: `fitri@${getEmailDomain()}`, role: 'student' as UserRole },
    { id: 'user7', name: 'Gunawan', email: `gunawan@${getEmailDomain()}`, role: 'student' as UserRole },
    { id: 'user8', name: 'Hartono', email: `hartono@${getEmailDomain()}`, role: 'parent' as UserRole },
    { id: 'user9', name: 'Indah Permata', email: `indah@${getEmailDomain()}`, role: 'parent' as UserRole },
    { id: 'user10', name: 'Joko Susilo', email: `joko@${getEmailDomain()}`, role: 'student' as UserRole, extraRole: 'osis' as UserExtraRole },
  ],
};

export type SchoolDocumentsConfig = typeof SCHOOL_DOCUMENTS;

export type SchoolConfig = typeof ENV.SCHOOL;
export type EmailConfig = typeof ENV.EMAIL;
export type ExternalConfig = typeof ENV.EXTERNAL;
export type MockContactsConfig = typeof MOCK_CONTACTS;
